import { useState, useEffect, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  Upload,
  X,
  Building2,
  FileText,
  Mail,
  ImageIcon,
  CheckCircle2,
  AlertCircle,
  CalendarClock,
  HardDrive,
} from 'lucide-react';
import { apiFetch } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { cn } from '@/lib/utils';

// ─── Types ──────────────────────────────────────────────────────────────────

interface AppSettings {
  id: number;
  name: string | null;
  address: string | null;
  phone: string | null;
  email: string | null;
  website: string | null;
  logo_path: string | null;
  estimate_terms: string | null;
  po_terms: string | null;
  smtp_host: string | null;
  smtp_port: number | null;
  smtp_user: string | null;
  smtp_from: string | null;
  smtp_pass_set: boolean;
  smtp_configured: boolean;
  cash_email_recipients: string | null;
  backup_email_recipients: string | null;
  daily_cash_email_enabled: number | null;
  weekly_cash_email_enabled: number | null;
  weekly_cash_email_dow: number | null;
  backup_email_enabled: number | null;
  scheduled_email_time: string | null;
  daily_cash_email_time: string | null;
  weekly_cash_email_time: string | null;
  backup_email_time: string | null;
  alert_emails: string | null;
  alerts_enabled: number | null;
  daily_backup_enabled: number | null;
  local_backup_time: string | null;
  digest_recipients: string | null;
  digest_dow: number | null;
  digest_time: string | null;
  low_stock_digest_enabled: number | null;
  aging_digest_enabled: number | null;
  gdrive_backup_enabled: number | null;
  gdrive_folder_id: string | null;
  gdrive_oauth_client_id: string | null;
  gdrive_oauth_secret_set: boolean;
  gdrive_connected: boolean;
  gdrive_account_email: string | null;
  updated_at: string | null;
}

// ─── Scheduled job status ────────────────────────────────────────────────────

interface ScheduledJob {
  job: string;
  label: string;
  enabled: boolean;
  schedule: string;
  last_run: string | null;
  next_run: string | null;
}

interface ScheduledStatusResponse {
  data: { jobs: ScheduledJob[] };
}

interface SettingsResponse {
  data: { settings: AppSettings };
}

// ─── Form state ──────────────────────────────────────────────────────────────

interface FormState {
  name: string;
  address: string;
  phone: string;
  email: string;
  website: string;
  estimate_terms: string;
  po_terms: string;
  smtp_host: string;
  smtp_port: string;
  smtp_user: string;
  smtp_from: string;
  smtp_pass: string;
  cash_email_recipients: string;
  backup_email_recipients: string;
  daily_cash_email_enabled: boolean;
  weekly_cash_email_enabled: boolean;
  weekly_cash_email_dow: string;
  backup_email_enabled: boolean;
  daily_cash_email_time: string;
  weekly_cash_email_time: string;
  backup_email_time: string;
  alerts_enabled: boolean;
  alert_emails: string;
  daily_backup_enabled: boolean;
  local_backup_time: string;
  digest_recipients: string;
  digest_dow: string;
  digest_time: string;
  low_stock_digest_enabled: boolean;
  aging_digest_enabled: boolean;
  gdrive_backup_enabled: boolean;
  gdrive_folder_id: string;
  gdrive_oauth_client_id: string;
}

function toFormState(s: AppSettings): FormState {
  return {
    name: s.name ?? '',
    address: s.address ?? '',
    phone: s.phone ?? '',
    email: s.email ?? '',
    website: s.website ?? '',
    estimate_terms: s.estimate_terms ?? '',
    po_terms: s.po_terms ?? '',
    smtp_host: s.smtp_host ?? '',
    smtp_port: s.smtp_port != null ? String(s.smtp_port) : '',
    smtp_user: s.smtp_user ?? '',
    smtp_from: s.smtp_from ?? '',
    smtp_pass: '',
    cash_email_recipients: s.cash_email_recipients ?? '',
    backup_email_recipients: s.backup_email_recipients ?? '',
    daily_cash_email_enabled: !!s.daily_cash_email_enabled,
    weekly_cash_email_enabled: !!s.weekly_cash_email_enabled,
    weekly_cash_email_dow: s.weekly_cash_email_dow != null ? String(s.weekly_cash_email_dow) : '1',
    backup_email_enabled: !!s.backup_email_enabled,
    daily_cash_email_time: s.daily_cash_email_time ?? '',
    weekly_cash_email_time: s.weekly_cash_email_time ?? '',
    backup_email_time: s.backup_email_time ?? '',
    alerts_enabled: !!s.alerts_enabled,
    alert_emails: s.alert_emails ?? '',
    daily_backup_enabled: !!s.daily_backup_enabled,
    local_backup_time: s.local_backup_time ?? '',
    digest_recipients: s.digest_recipients ?? '',
    digest_dow: s.digest_dow != null ? String(s.digest_dow) : '1',
    digest_time: s.digest_time ?? '',
    low_stock_digest_enabled: !!s.low_stock_digest_enabled,
    aging_digest_enabled: !!s.aging_digest_enabled,
    gdrive_backup_enabled: !!s.gdrive_backup_enabled,
    gdrive_folder_id: s.gdrive_folder_id ?? '',
    gdrive_oauth_client_id: s.gdrive_oauth_client_id ?? '',
  };
}

// ─── Shared textarea style ───────────────────────────────────────────────────

const textareaClass = cn(
  'flex min-h-[100px] w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm',
  'placeholder:text-muted-foreground resize-y',
  'focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring',
  'disabled:cursor-not-allowed disabled:opacity-50',
);

const selectClass = cn(
  'flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm transition-colors',
  'focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring',
  'disabled:cursor-not-allowed disabled:opacity-50',
);

const DOW_OPTIONS = [
  { value: '0', label: 'Sunday' },
  { value: '1', label: 'Monday' },
  { value: '2', label: 'Tuesday' },
  { value: '3', label: 'Wednesday' },
  { value: '4', label: 'Thursday' },
  { value: '5', label: 'Friday' },
  { value: '6', label: 'Saturday' },
];

// Format an ISO / date string for the status table; '—' when null/unparseable
function formatRunTime(value: string | null): string {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

// ─── Inline checkbox row ─────────────────────────────────────────────────────

function CheckboxRow({
  id,
  checked,
  onChange,
  disabled,
  children,
}: {
  id: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  disabled?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div className="flex items-center gap-2.5">
      <input
        id={id}
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        disabled={disabled}
        className="h-4 w-4 rounded border-gray-300 accent-primary"
      />
      <Label htmlFor={id} className="cursor-pointer font-normal">
        {children}
      </Label>
    </div>
  );
}

// ─── Section heading ─────────────────────────────────────────────────────────

function SectionIcon({ icon: Icon, className }: { icon: React.ElementType; className?: string }) {
  return (
    <div className={cn('flex h-8 w-8 items-center justify-center rounded-md', className)}>
      <Icon className="h-4 w-4" />
    </div>
  );
}

// ─── Tab bar ─────────────────────────────────────────────────────────────────

type SettingsTab = 'company' | 'email' | 'notifications' | 'backups';

const SETTINGS_TABS: { value: SettingsTab; label: string; icon: React.ElementType }[] = [
  { value: 'company', label: 'Company', icon: Building2 },
  { value: 'email', label: 'Email', icon: Mail },
  { value: 'notifications', label: 'Notifications', icon: CalendarClock },
  { value: 'backups', label: 'Backups', icon: HardDrive },
];

// ─── Main page ───────────────────────────────────────────────────────────────

export default function SettingsPage() {
  const queryClient = useQueryClient();
  const initRef = useRef(false);

  const [tab, setTab] = useState<SettingsTab>('company');

  const [form, setForm] = useState<FormState>({
    name: '',
    address: '',
    phone: '',
    email: '',
    website: '',
    estimate_terms: '',
    po_terms: '',
    smtp_host: '',
    smtp_port: '',
    smtp_user: '',
    smtp_from: '',
    smtp_pass: '',
    cash_email_recipients: '',
    backup_email_recipients: '',
    daily_cash_email_enabled: false,
    weekly_cash_email_enabled: false,
    weekly_cash_email_dow: '1',
    backup_email_enabled: false,
    daily_cash_email_time: '',
    weekly_cash_email_time: '',
    backup_email_time: '',
    alerts_enabled: false,
    alert_emails: '',
    daily_backup_enabled: false,
    local_backup_time: '',
    digest_recipients: '',
    digest_dow: '1',
    digest_time: '',
    low_stock_digest_enabled: false,
    aging_digest_enabled: false,
    gdrive_backup_enabled: false,
    gdrive_folder_id: '',
    gdrive_oauth_client_id: '',
  });
  // Write-only OAuth client secret — kept out of the saved FormState round-trip
  // (mirrors the smtp_pass secret pattern). Only sent in PATCH when non-empty,
  // cleared after a successful save.
  const [gdriveClientSecret, setGdriveClientSecret] = useState('');
  // OAuth "connect" dance — both are transient local state (not persisted):
  // the consent URL returned by /gdrive-auth-url, and the code the owner pastes.
  const [gdriveAuthUrl, setGdriveAuthUrl] = useState<string | null>(null);
  const [gdriveCode, setGdriveCode] = useState('');
  const [gdriveAuthUrlLoading, setGdriveAuthUrlLoading] = useState(false);
  const [gdriveConnecting, setGdriveConnecting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testRunning, setTestRunning] = useState<
    null | 'daily_cash' | 'weekly_cash' | 'backup' | 'low_stock' | 'aging'
  >(null);
  const [gdriveTesting, setGdriveTesting] = useState(false);
  const [logoUploading, setLogoUploading] = useState(false);
  const [logoRemoving, setLogoRemoving] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ['settings'],
    queryFn: () => apiFetch<SettingsResponse>('/api/settings'),
  });

  const { data: statusData } = useQuery({
    queryKey: ['scheduled-status'],
    queryFn: () => apiFetch<ScheduledStatusResponse>('/api/settings/scheduled-status'),
  });

  const settings = data?.data.settings;
  const scheduledJobs = statusData?.data.jobs ?? [];

  // Initialize form once when data first loads — ref guard prevents clobbering edits
  useEffect(() => {
    if (settings && !initRef.current) {
      initRef.current = true;
      setForm(toFormState(settings));
    }
  }, [settings]);

  function setField<K extends keyof FormState>(key: K, value: FormState[K]) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  // ── Save settings ────────────────────────────────────────────────────────

  async function handleSave(e: React.FormEvent) {
    e.preventDefault();
    setSaving(true);
    try {
      const body: Record<string, unknown> = {
        name: form.name,
        address: form.address,
        phone: form.phone,
        email: form.email,
        website: form.website,
        estimate_terms: form.estimate_terms,
        po_terms: form.po_terms,
        smtp_host: form.smtp_host,
        smtp_port: form.smtp_port ? Number(form.smtp_port) : null,
        smtp_user: form.smtp_user,
        smtp_from: form.smtp_from,
        cash_email_recipients: form.cash_email_recipients,
        backup_email_recipients: form.backup_email_recipients,
        daily_cash_email_enabled: form.daily_cash_email_enabled ? 1 : 0,
        weekly_cash_email_enabled: form.weekly_cash_email_enabled ? 1 : 0,
        weekly_cash_email_dow: Number(form.weekly_cash_email_dow),
        backup_email_enabled: form.backup_email_enabled ? 1 : 0,
        daily_cash_email_time: form.daily_cash_email_time || null,
        weekly_cash_email_time: form.weekly_cash_email_time || null,
        backup_email_time: form.backup_email_time || null,
        alerts_enabled: form.alerts_enabled ? 1 : 0,
        alert_emails: form.alert_emails,
        daily_backup_enabled: form.daily_backup_enabled ? 1 : 0,
        local_backup_time: form.local_backup_time || null,
        digest_recipients: form.digest_recipients,
        digest_dow: Number(form.digest_dow),
        digest_time: form.digest_time || null,
        low_stock_digest_enabled: form.low_stock_digest_enabled ? 1 : 0,
        aging_digest_enabled: form.aging_digest_enabled ? 1 : 0,
        gdrive_backup_enabled: form.gdrive_backup_enabled ? 1 : 0,
        gdrive_folder_id: form.gdrive_folder_id,
        gdrive_oauth_client_id: form.gdrive_oauth_client_id,
      };
      // Only include smtp_pass if user typed something
      if (form.smtp_pass.trim()) {
        body.smtp_pass = form.smtp_pass;
      }
      // Write-only secret: only send the OAuth client secret when the user typed a new one
      if (gdriveClientSecret.trim()) {
        body.gdrive_oauth_client_secret = gdriveClientSecret;
      }
      await apiFetch('/api/settings', { method: 'PATCH', body: JSON.stringify(body) });
      toast.success('Settings saved');
      setGdriveClientSecret(''); // clear write-only field after a successful save
      queryClient.invalidateQueries({ queryKey: ['settings'] });
      queryClient.invalidateQueries({ queryKey: ['scheduled-status'] });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  }

  // ── Run a scheduled job now (test) ─────────────────────────────────────────

  async function handleRunTest(
    job: 'daily_cash' | 'weekly_cash' | 'backup' | 'low_stock' | 'aging',
  ) {
    setTestRunning(job);
    try {
      const res = await apiFetch<{ data: { ok: boolean; message: string } }>(
        '/api/settings/run-scheduled',
        { method: 'POST', body: JSON.stringify({ job }) },
      );
      const { ok, message } = res.data;
      if (ok) {
        toast.success(message);
      } else {
        toast.warning(message);
      }
      queryClient.invalidateQueries({ queryKey: ['scheduled-status'] });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to run job');
    } finally {
      setTestRunning(null);
    }
  }

  // ── Test Google Drive off-site backup connectivity ─────────────────────────

  async function handleTestGdrive() {
    setGdriveTesting(true);
    try {
      const res = await apiFetch<{ data: { ok: boolean; message: string } }>(
        '/api/settings/test-gdrive',
        { method: 'POST' },
      );
      const { ok, message } = res.data;
      if (ok) {
        toast.success(message);
      } else {
        toast.warning(message);
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to test Google Drive');
    } finally {
      setGdriveTesting(false);
    }
  }

  // ── Google Drive OAuth: fetch the consent URL the owner opens to authorize ──

  async function handleGetAuthUrl() {
    setGdriveAuthUrlLoading(true);
    try {
      const res = await apiFetch<{ data: { url: string } }>('/api/settings/gdrive-auth-url', {
        method: 'POST',
      });
      setGdriveAuthUrl(res.data.url);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to get authorization link');
    } finally {
      setGdriveAuthUrlLoading(false);
    }
  }

  // ── Google Drive OAuth: exchange the pasted code for a refresh token ────────

  async function handleGdriveConnect() {
    setGdriveConnecting(true);
    try {
      const res = await apiFetch<{ data: { ok: boolean; message: string; email?: string | null } }>(
        '/api/settings/gdrive-connect',
        { method: 'POST', body: JSON.stringify({ code: gdriveCode.trim() }) },
      );
      const { ok, message } = res.data;
      if (ok) {
        toast.success(message);
        setGdriveCode(''); // clear the one-time code after a successful exchange
        setGdriveAuthUrl(null); // the consent link is spent
        queryClient.invalidateQueries({ queryKey: ['settings'] });
      } else {
        toast.warning(message);
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to connect Google Drive');
    } finally {
      setGdriveConnecting(false);
    }
  }

  // ── Logo upload ──────────────────────────────────────────────────────────

  async function handleLogoUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';

    setLogoUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      // Do NOT set Content-Type — browser sets multipart boundary automatically
      const res = await fetch('/api/settings/logo', {
        method: 'POST',
        credentials: 'include',
        body: formData,
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error((body as { error?: string }).error ?? `HTTP ${res.status}`);
      }
      toast.success('Logo uploaded');
      queryClient.invalidateQueries({ queryKey: ['settings'] });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to upload logo');
    } finally {
      setLogoUploading(false);
    }
  }

  // ── Logo remove ──────────────────────────────────────────────────────────

  async function handleLogoRemove() {
    setLogoRemoving(true);
    try {
      await apiFetch('/api/settings/logo', { method: 'DELETE' });
      toast.success('Logo removed');
      queryClient.invalidateQueries({ queryKey: ['settings'] });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to remove logo');
    } finally {
      setLogoRemoving(false);
    }
  }

  // ─── Render ───────────────────────────────────────────────────────────────

  if (isLoading) {
    return (
      <div className="p-6">
        <div className="mb-6">
          <h1 className="text-2xl font-bold tracking-tight">Settings</h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            Configure your company and application settings
          </p>
        </div>
        <div className="flex items-center justify-center py-20 text-muted-foreground text-sm">
          Loading settings…
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="mb-6">
          <h1 className="text-2xl font-bold tracking-tight">Settings</h1>
        </div>
        <div className="rounded-md border border-destructive/50 bg-destructive/10 px-4 py-3 text-sm text-destructive">
          Failed to load settings: {error instanceof Error ? error.message : 'Unknown error'}
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-3xl">
      {/* Page header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold tracking-tight">Settings</h1>
        <p className="text-sm text-muted-foreground mt-0.5">
          Configure your company profile, document terms, and email settings
        </p>
      </div>

      {/* Tab bar */}
      <div className="mb-6 flex w-fit items-center rounded-md border bg-muted/40 p-0.5 gap-0.5">
        {SETTINGS_TABS.map((t) => {
          const Icon = t.icon;
          return (
            <button
              key={t.value}
              type="button"
              onClick={() => setTab(t.value)}
              className={cn(
                'inline-flex items-center gap-1.5 px-3 py-1.5 rounded text-sm font-medium transition-colors',
                tab === t.value
                  ? 'bg-background text-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground',
              )}
            >
              <Icon className="h-3.5 w-3.5" />
              {t.label}
            </button>
          );
        })}
      </div>

      <form onSubmit={handleSave} className="space-y-6">
        {/* ════ COMPANY TAB ════ */}
        {tab === 'company' && (
          <>
            {/* ── Company Profile ─────────────────────────────────────────────── */}
            <Card>
              <CardHeader className="pb-4">
                <div className="flex items-center gap-3">
                  <SectionIcon icon={Building2} className="bg-blue-50 text-blue-600" />
                  <div>
                    <CardTitle className="text-base">Company Profile</CardTitle>
                    <CardDescription className="text-xs mt-0.5">
                      Appears on all printed documents and PDFs
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-1.5">
                  <Label htmlFor="settings-name">Company Name</Label>
                  <Input
                    id="settings-name"
                    value={form.name}
                    onChange={(e) => setField('name', e.target.value)}
                    placeholder="Tile & Flooring Co."
                    disabled={saving}
                  />
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="settings-address">Address</Label>
                  <textarea
                    id="settings-address"
                    className={textareaClass}
                    value={form.address}
                    onChange={(e) => setField('address', e.target.value)}
                    placeholder={'123 Main St\nCity, Province A1B 2C3'}
                    rows={3}
                    disabled={saving}
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <Label htmlFor="settings-phone">Phone</Label>
                    <Input
                      id="settings-phone"
                      type="tel"
                      value={form.phone}
                      onChange={(e) => setField('phone', e.target.value)}
                      placeholder="+1 (555) 000-0000"
                      disabled={saving}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="settings-email">Email</Label>
                    <Input
                      id="settings-email"
                      type="email"
                      value={form.email}
                      onChange={(e) => setField('email', e.target.value)}
                      placeholder="info@yourcompany.com"
                      disabled={saving}
                    />
                  </div>
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="settings-website">Website</Label>
                  <Input
                    id="settings-website"
                    type="url"
                    value={form.website}
                    onChange={(e) => setField('website', e.target.value)}
                    placeholder="https://yourcompany.com"
                    disabled={saving}
                  />
                </div>
              </CardContent>
            </Card>

            {/* ── Logo ────────────────────────────────────────────────────────── */}
            <Card>
              <CardHeader className="pb-4">
                <div className="flex items-center gap-3">
                  <SectionIcon icon={ImageIcon} className="bg-purple-50 text-purple-600" />
                  <div>
                    <CardTitle className="text-base">Company Logo</CardTitle>
                    <CardDescription className="text-xs mt-0.5">
                      Used on PDF headers — PNG or JPEG recommended
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="flex flex-col sm:flex-row items-start gap-5">
                  {/* Preview */}
                  <div
                    className={cn(
                      'flex items-center justify-center shrink-0 rounded-lg border-2 border-dashed',
                      'w-full sm:w-48 h-24',
                      settings?.logo_path
                        ? 'border-transparent bg-muted/30'
                        : 'border-muted-foreground/20 bg-muted/20',
                    )}
                  >
                    {settings?.logo_path ? (
                      <img
                        src={`/api/settings/logo?t=${settings.updated_at ?? ''}`}
                        alt="Company logo"
                        className="max-h-20 max-w-full object-contain rounded"
                      />
                    ) : (
                      <div className="flex flex-col items-center gap-1.5 text-muted-foreground">
                        <ImageIcon className="h-8 w-8 opacity-30" />
                        <span className="text-xs">No logo uploaded</span>
                      </div>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="flex flex-col gap-2.5 flex-1">
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={handleLogoUpload}
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={logoUploading || logoRemoving}
                      className="w-fit"
                    >
                      <Upload className="h-3.5 w-3.5 mr-1.5" />
                      {logoUploading
                        ? 'Uploading…'
                        : settings?.logo_path
                          ? 'Replace Logo'
                          : 'Upload Logo'}
                    </Button>

                    {settings?.logo_path && (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={handleLogoRemove}
                        disabled={logoUploading || logoRemoving}
                        className="w-fit text-destructive hover:text-destructive border-destructive/30 hover:border-destructive/60 hover:bg-destructive/5"
                      >
                        <X className="h-3.5 w-3.5 mr-1.5" />
                        {logoRemoving ? 'Removing…' : 'Remove Logo'}
                      </Button>
                    )}

                    <p className="text-xs text-muted-foreground mt-0.5">
                      Max 5 MB. Transparent PNG works best on white PDF backgrounds.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* ── Document Terms ──────────────────────────────────────────────── */}
            <Card>
              <CardHeader className="pb-4">
                <div className="flex items-center gap-3">
                  <SectionIcon icon={FileText} className="bg-amber-50 text-amber-600" />
                  <div>
                    <CardTitle className="text-base">Document Terms</CardTitle>
                    <CardDescription className="text-xs mt-0.5">
                      Printed in the footer of each document type
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-1.5">
                  <Label htmlFor="settings-estimate-terms">Estimate / Invoice Terms</Label>
                  <textarea
                    id="settings-estimate-terms"
                    className={textareaClass}
                    value={form.estimate_terms}
                    onChange={(e) => setField('estimate_terms', e.target.value)}
                    placeholder="Payment due upon receipt. All sales final."
                    rows={4}
                    disabled={saving}
                  />
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="settings-po-terms">Purchase Order Terms</Label>
                  <textarea
                    id="settings-po-terms"
                    className={textareaClass}
                    value={form.po_terms}
                    onChange={(e) => setField('po_terms', e.target.value)}
                    placeholder="Prices subject to change. Delivery as agreed."
                    rows={4}
                    disabled={saving}
                  />
                </div>
              </CardContent>
            </Card>
          </>
        )}

        {/* ════ EMAIL TAB ════ */}
        {tab === 'email' && (
          <>
            {/* ── SMTP / Email ─────────────────────────────────────────────────── */}
            <Card>
              <CardHeader className="pb-4">
                <div className="flex items-center gap-3">
                  <SectionIcon icon={Mail} className="bg-green-50 text-green-600" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <CardTitle className="text-base">Email (SMTP)</CardTitle>
                      {settings?.smtp_configured ? (
                        <span className="inline-flex items-center gap-1 rounded-full bg-green-50 px-2 py-0.5 text-[11px] font-medium text-green-700 border border-green-200">
                          <CheckCircle2 className="h-3 w-3" />
                          Configured
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-[11px] font-medium text-muted-foreground border border-border">
                          <AlertCircle className="h-3 w-3" />
                          Not configured
                        </span>
                      )}
                    </div>
                    <CardDescription className="text-xs mt-0.5">
                      Used to send estimates and returns to customers
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <Label htmlFor="settings-smtp-host">SMTP Host</Label>
                    <Input
                      id="settings-smtp-host"
                      value={form.smtp_host}
                      onChange={(e) => setField('smtp_host', e.target.value)}
                      placeholder="smtp.gmail.com"
                      disabled={saving}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="settings-smtp-port">Port</Label>
                    <Input
                      id="settings-smtp-port"
                      type="number"
                      value={form.smtp_port}
                      onChange={(e) => setField('smtp_port', e.target.value)}
                      placeholder="587"
                      min={1}
                      max={65535}
                      disabled={saving}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <Label htmlFor="settings-smtp-user">Username</Label>
                    <Input
                      id="settings-smtp-user"
                      value={form.smtp_user}
                      onChange={(e) => setField('smtp_user', e.target.value)}
                      placeholder="you@gmail.com"
                      autoComplete="off"
                      disabled={saving}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="settings-smtp-from">From Address</Label>
                    <Input
                      id="settings-smtp-from"
                      type="email"
                      value={form.smtp_from}
                      onChange={(e) => setField('smtp_from', e.target.value)}
                      placeholder="noreply@yourcompany.com"
                      disabled={saving}
                    />
                  </div>
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="settings-smtp-pass">Password / App Password</Label>
                  <Input
                    id="settings-smtp-pass"
                    type="password"
                    value={form.smtp_pass}
                    onChange={(e) => setField('smtp_pass', e.target.value)}
                    placeholder={
                      settings?.smtp_pass_set ? '•••••••• (set — leave blank to keep)' : 'Not set'
                    }
                    autoComplete="new-password"
                    disabled={saving}
                  />
                  <p className="text-xs text-muted-foreground">
                    Leave blank to keep the current password.
                  </p>
                </div>
              </CardContent>
            </Card>
          </>
        )}

        {/* ════ NOTIFICATIONS TAB ════ */}
        {tab === 'notifications' && (
          <Card>
            <CardHeader className="pb-4">
              <div className="flex items-center gap-3">
                <SectionIcon icon={CalendarClock} className="bg-rose-50 text-rose-600" />
                <div>
                  <CardTitle className="text-base">Scheduled Emails</CardTitle>
                  <CardDescription className="text-xs mt-0.5">
                    Automatic cash statements, digests, and failure alerts sent on a schedule
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-5">
              {/* Cash statement recipients */}
              <div className="space-y-1.5">
                <Label htmlFor="settings-cash-recipients">Cash statement recipients</Label>
                <Input
                  id="settings-cash-recipients"
                  value={form.cash_email_recipients}
                  onChange={(e) => setField('cash_email_recipients', e.target.value)}
                  placeholder="owner@shop.com, accountant@shop.com"
                  disabled={saving}
                />
                <p className="text-xs text-muted-foreground">
                  Comma-separated. Daily &amp; weekly cash statement CSVs go here.
                </p>
              </div>

              {/* Cash statement schedule */}
              <div className="space-y-4 rounded-md border border-border/60 bg-muted/20 p-3.5">
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                  Cash statements
                </p>

                {/* Daily cash: toggle + per-job time */}
                <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto] sm:items-end gap-3">
                  <CheckboxRow
                    id="settings-daily-cash-enabled"
                    checked={form.daily_cash_email_enabled}
                    onChange={(v) => setField('daily_cash_email_enabled', v)}
                    disabled={saving}
                  >
                    Email daily cash statement (yesterday) every morning
                  </CheckboxRow>
                  <div className="space-y-1.5">
                    <Label htmlFor="settings-daily-cash-time" className="text-xs">
                      Daily cash send time
                    </Label>
                    <Input
                      id="settings-daily-cash-time"
                      type="time"
                      value={form.daily_cash_email_time}
                      onChange={(e) => setField('daily_cash_email_time', e.target.value)}
                      disabled={saving || !form.daily_cash_email_enabled}
                      className="sm:w-32"
                    />
                  </div>
                </div>

                {/* Weekly cash: toggle + day + per-job time */}
                <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto] sm:items-end gap-3">
                  <CheckboxRow
                    id="settings-weekly-cash-enabled"
                    checked={form.weekly_cash_email_enabled}
                    onChange={(v) => setField('weekly_cash_email_enabled', v)}
                    disabled={saving}
                  >
                    Email full cash statement weekly
                  </CheckboxRow>
                  <div className="space-y-1.5">
                    <Label htmlFor="settings-weekly-cash-time" className="text-xs">
                      Weekly cash send time
                    </Label>
                    <Input
                      id="settings-weekly-cash-time"
                      type="time"
                      value={form.weekly_cash_email_time}
                      onChange={(e) => setField('weekly_cash_email_time', e.target.value)}
                      disabled={saving || !form.weekly_cash_email_enabled}
                      className="sm:w-32"
                    />
                  </div>
                </div>
                <div className="space-y-1.5 sm:max-w-[14rem]">
                  <Label htmlFor="settings-weekly-dow" className="text-xs">
                    Weekly send day
                  </Label>
                  <select
                    id="settings-weekly-dow"
                    className={selectClass}
                    value={form.weekly_cash_email_dow}
                    onChange={(e) => setField('weekly_cash_email_dow', e.target.value)}
                    disabled={saving || !form.weekly_cash_email_enabled}
                  >
                    {DOW_OPTIONS.map((d) => (
                      <option key={d.value} value={d.value}>
                        {d.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Digests */}
              <div className="space-y-4 rounded-md border border-border/60 bg-muted/20 p-3.5">
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                  Digests
                </p>

                <CheckboxRow
                  id="settings-low-stock-digest-enabled"
                  checked={form.low_stock_digest_enabled}
                  onChange={(v) => setField('low_stock_digest_enabled', v)}
                  disabled={saving}
                >
                  Email low-stock digest weekly
                </CheckboxRow>

                <CheckboxRow
                  id="settings-aging-digest-enabled"
                  checked={form.aging_digest_enabled}
                  onChange={(v) => setField('aging_digest_enabled', v)}
                  disabled={saving}
                >
                  Email receivables &amp; payables aging digest weekly
                </CheckboxRow>

                {/* Shared digest day + time (disabled when both toggles off) */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label htmlFor="settings-digest-dow" className="text-xs">
                      Digest send day
                    </Label>
                    <select
                      id="settings-digest-dow"
                      className={selectClass}
                      value={form.digest_dow}
                      onChange={(e) => setField('digest_dow', e.target.value)}
                      disabled={
                        saving || (!form.low_stock_digest_enabled && !form.aging_digest_enabled)
                      }
                    >
                      {DOW_OPTIONS.map((d) => (
                        <option key={d.value} value={d.value}>
                          {d.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="settings-digest-time" className="text-xs">
                      Digest send time
                    </Label>
                    <Input
                      id="settings-digest-time"
                      type="time"
                      value={form.digest_time}
                      onChange={(e) => setField('digest_time', e.target.value)}
                      disabled={
                        saving || (!form.low_stock_digest_enabled && !form.aging_digest_enabled)
                      }
                      className="sm:w-32"
                    />
                  </div>
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="settings-digest-recipients">Digest recipients</Label>
                  <Input
                    id="settings-digest-recipients"
                    value={form.digest_recipients}
                    onChange={(e) => setField('digest_recipients', e.target.value)}
                    placeholder="owner@shop.com, manager@shop.com"
                    disabled={saving}
                  />
                  <p className="text-xs text-muted-foreground">
                    Comma-separated. Low-stock and aging digests go here.
                  </p>
                </div>
              </div>

              {/* Failure alerts */}
              <div className="space-y-3 rounded-md border border-border/60 bg-muted/20 p-3.5">
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                  Failure alerts
                </p>
                <CheckboxRow
                  id="settings-alerts-enabled"
                  checked={form.alerts_enabled}
                  onChange={(v) => setField('alerts_enabled', v)}
                  disabled={saving}
                >
                  Email me if a scheduled job fails
                </CheckboxRow>
                <div className="space-y-1.5">
                  <Label htmlFor="settings-alert-emails">Alert recipients</Label>
                  <Input
                    id="settings-alert-emails"
                    value={form.alert_emails}
                    onChange={(e) => setField('alert_emails', e.target.value)}
                    placeholder="owner@shop.com, admin@shop.com"
                    disabled={saving || !form.alerts_enabled}
                  />
                  <p className="text-xs text-muted-foreground">
                    Comma-separated. Defaults to your company email if blank.
                  </p>
                </div>
              </div>

              {/* Send test buttons */}
              <div className="space-y-2">
                <p className="text-xs text-muted-foreground">
                  Run a job now to verify SMTP and recipients. Sends to the addresses above.
                </p>
                <div className="flex flex-wrap gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => handleRunTest('daily_cash')}
                    disabled={testRunning !== null}
                  >
                    {testRunning === 'daily_cash' ? 'Sending…' : 'Send test daily cash'}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => handleRunTest('weekly_cash')}
                    disabled={testRunning !== null}
                  >
                    {testRunning === 'weekly_cash' ? 'Sending…' : 'Send test weekly cash'}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => handleRunTest('low_stock')}
                    disabled={testRunning !== null}
                  >
                    {testRunning === 'low_stock' ? 'Sending…' : 'Send test low-stock'}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => handleRunTest('aging')}
                    disabled={testRunning !== null}
                  >
                    {testRunning === 'aging' ? 'Sending…' : 'Send test aging'}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* ════ BACKUPS TAB ════ */}
        {tab === 'backups' && (
          <Card>
            <CardHeader className="pb-4">
              <div className="flex items-center gap-3">
                <SectionIcon icon={HardDrive} className="bg-sky-50 text-sky-600" />
                <div>
                  <CardTitle className="text-base">Backups</CardTitle>
                  <CardDescription className="text-xs mt-0.5">
                    Nightly local backups, emailed copies, and off-site Google Drive uploads
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-5">
              {/* Backup recipients */}
              <div className="space-y-1.5">
                <Label htmlFor="settings-backup-recipients">Backup recipients</Label>
                <Input
                  id="settings-backup-recipients"
                  value={form.backup_email_recipients}
                  onChange={(e) => setField('backup_email_recipients', e.target.value)}
                  placeholder="owner@shop.com"
                  disabled={saving}
                />
                <p className="text-xs text-muted-foreground">Comma-separated.</p>
              </div>

              {/* Backup schedule */}
              <div className="space-y-4 rounded-md border border-border/60 bg-muted/20 p-3.5">
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                  Backups
                </p>

                {/* Email backup: toggle + per-job time */}
                <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto] sm:items-end gap-3">
                  <CheckboxRow
                    id="settings-backup-email-enabled"
                    checked={form.backup_email_enabled}
                    onChange={(v) => setField('backup_email_enabled', v)}
                    disabled={saving}
                  >
                    Email daily backup
                  </CheckboxRow>
                  <div className="space-y-1.5">
                    <Label htmlFor="settings-backup-email-time" className="text-xs">
                      Backup send time
                    </Label>
                    <Input
                      id="settings-backup-email-time"
                      type="time"
                      value={form.backup_email_time}
                      onChange={(e) => setField('backup_email_time', e.target.value)}
                      disabled={saving || !form.backup_email_enabled}
                      className="sm:w-32"
                    />
                  </div>
                </div>

                {/* Local backup: toggle + time */}
                <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto] sm:items-end gap-3">
                  <CheckboxRow
                    id="settings-daily-backup-enabled"
                    checked={form.daily_backup_enabled}
                    onChange={(v) => setField('daily_backup_enabled', v)}
                    disabled={saving}
                  >
                    Keep nightly local backup
                  </CheckboxRow>
                  <div className="space-y-1.5">
                    <Label htmlFor="settings-local-backup-time" className="text-xs">
                      Local backup time
                    </Label>
                    <Input
                      id="settings-local-backup-time"
                      type="time"
                      value={form.local_backup_time}
                      onChange={(e) => setField('local_backup_time', e.target.value)}
                      disabled={saving || !form.daily_backup_enabled}
                      className="sm:w-32"
                    />
                  </div>
                </div>
              </div>

              {/* Off-site backup (Google Drive) */}
              <div className="space-y-4 rounded-md border border-border/60 bg-muted/20 p-3.5">
                <div className="space-y-1">
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                    Off-site backup (Google Drive)
                  </p>
                  <p className="text-xs text-muted-foreground">
                    Back up to your own Google Drive. Create a Desktop OAuth client in Google Cloud,
                    then connect below.
                  </p>
                </div>

                {/* Step 1 — Credentials */}
                <div className="space-y-3 rounded-md border border-border/60 bg-background/60 p-3.5">
                  <p className="text-sm font-medium">1. Credentials</p>

                  <div className="space-y-1.5">
                    <Label htmlFor="settings-gdrive-client-id">OAuth Client ID</Label>
                    <Input
                      id="settings-gdrive-client-id"
                      value={form.gdrive_oauth_client_id}
                      onChange={(e) => setField('gdrive_oauth_client_id', e.target.value)}
                      placeholder="1234567890-abc.apps.googleusercontent.com"
                      autoComplete="off"
                      spellCheck={false}
                      disabled={saving}
                    />
                  </div>

                  <div className="space-y-1.5">
                    <div className="flex items-center gap-2">
                      <Label htmlFor="settings-gdrive-client-secret">Client Secret</Label>
                      {settings?.gdrive_oauth_secret_set && (
                        <span className="inline-flex items-center gap-1 rounded-full bg-green-50 px-2 py-0.5 text-[11px] font-medium text-green-700 border border-green-200">
                          <CheckCircle2 className="h-3 w-3" />
                          Saved
                        </span>
                      )}
                    </div>
                    <Input
                      id="settings-gdrive-client-secret"
                      type="password"
                      value={gdriveClientSecret}
                      onChange={(e) => setGdriveClientSecret(e.target.value)}
                      placeholder="Paste client secret to set or replace"
                      autoComplete="new-password"
                      spellCheck={false}
                      disabled={saving}
                    />
                  </div>

                  <p className="text-xs text-muted-foreground">
                    Enter both, then click Save before connecting.
                  </p>
                </div>

                {/* Step 2 — Connect */}
                <div className="space-y-3 rounded-md border border-border/60 bg-background/60 p-3.5">
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-medium">2. Connect</p>
                    {settings?.gdrive_connected ? (
                      <span className="inline-flex items-center gap-1 rounded-full bg-green-50 px-2 py-0.5 text-[11px] font-medium text-green-700 border border-green-200">
                        <CheckCircle2 className="h-3 w-3" />
                        Connected as {settings.gdrive_account_email ?? 'your Google account'}
                      </span>
                    ) : (
                      <span className="text-xs text-muted-foreground">Not connected</span>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={handleGetAuthUrl}
                      disabled={saving || gdriveAuthUrlLoading}
                    >
                      {gdriveAuthUrlLoading ? 'Getting link…' : 'Get authorization link'}
                    </Button>

                    {gdriveAuthUrl && (
                      <div className="space-y-1.5 rounded-md border border-border/60 bg-muted/30 p-2.5">
                        <a
                          href={gdriveAuthUrl}
                          target="_blank"
                          rel="noreferrer"
                          className="block break-all text-xs font-medium text-blue-600 underline underline-offset-2 hover:text-blue-700"
                        >
                          {gdriveAuthUrl}
                        </a>
                        <p className="text-xs text-muted-foreground">
                          Open the link, choose your Google account, click Advanced → continue if
                          warned, approve, then copy the <span className="font-medium">code</span>{' '}
                          shown in the address bar (after <code>code=</code>).
                        </p>
                      </div>
                    )}
                  </div>

                  <div className="space-y-1.5">
                    <Label htmlFor="settings-gdrive-code">Authorization code</Label>
                    <div className="flex flex-col gap-2 sm:flex-row">
                      <Input
                        id="settings-gdrive-code"
                        value={gdriveCode}
                        onChange={(e) => setGdriveCode(e.target.value)}
                        placeholder="Paste the code from the address bar"
                        autoComplete="off"
                        spellCheck={false}
                        disabled={saving || gdriveConnecting}
                        className="flex-1"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={handleGdriveConnect}
                        disabled={saving || gdriveConnecting || !gdriveCode.trim()}
                        className="sm:w-auto"
                      >
                        {gdriveConnecting ? 'Connecting…' : 'Connect'}
                      </Button>
                    </div>
                  </div>
                </div>

                {/* Step 3 — Folder & enable */}
                <div className="space-y-3 rounded-md border border-border/60 bg-background/60 p-3.5">
                  <p className="text-sm font-medium">3. Folder &amp; enable</p>

                  <div className="space-y-1.5">
                    <Label htmlFor="settings-gdrive-folder-id">
                      Drive folder ID <span className="text-muted-foreground">(optional)</span>
                    </Label>
                    <Input
                      id="settings-gdrive-folder-id"
                      value={form.gdrive_folder_id}
                      onChange={(e) => setField('gdrive_folder_id', e.target.value)}
                      placeholder="Leave blank — Estflow makes its own folder"
                      autoComplete="off"
                      disabled={saving}
                    />
                    <p className="text-xs text-muted-foreground">
                      Leave blank (recommended) — Estflow creates and uses an{' '}
                      <strong>&ldquo;Estflow Backups&rdquo;</strong> folder in your Drive. Because
                      of the limited <code>drive.file</code> permission, only a folder Estflow
                      created can be used here.
                    </p>
                  </div>

                  <CheckboxRow
                    id="settings-gdrive-backup-enabled"
                    checked={form.gdrive_backup_enabled}
                    onChange={(v) => setField('gdrive_backup_enabled', v)}
                    disabled={saving}
                  >
                    Upload every nightly backup to Google Drive
                  </CheckboxRow>
                </div>

                <div>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handleTestGdrive}
                    disabled={saving || gdriveTesting}
                  >
                    {gdriveTesting ? 'Testing…' : 'Test Google Drive'}
                  </Button>
                </div>
              </div>

              {/* Send test button */}
              <div className="space-y-2">
                <p className="text-xs text-muted-foreground">
                  Run a job now to verify SMTP and recipients. Sends to the addresses above.
                </p>
                <div className="flex flex-wrap gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => handleRunTest('backup')}
                    disabled={testRunning !== null}
                  >
                    {testRunning === 'backup' ? 'Sending…' : 'Send test backup'}
                  </Button>
                </div>
              </div>

              {/* Scheduled jobs status */}
              <div className="space-y-2.5 pt-1">
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                  Scheduled jobs status
                </p>
                <div className="overflow-x-auto rounded-md border border-border/60">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-border/60 bg-muted/40 text-left text-xs text-muted-foreground">
                        <th className="px-3 py-2 font-medium">Job</th>
                        <th className="px-3 py-2 font-medium">Schedule</th>
                        <th className="px-3 py-2 font-medium text-center">Enabled</th>
                        <th className="px-3 py-2 font-medium whitespace-nowrap">Last run</th>
                        <th className="px-3 py-2 font-medium whitespace-nowrap">Next run</th>
                      </tr>
                    </thead>
                    <tbody>
                      {scheduledJobs.length === 0 ? (
                        <tr>
                          <td colSpan={5} className="px-3 py-4 text-center text-muted-foreground">
                            No scheduled jobs.
                          </td>
                        </tr>
                      ) : (
                        scheduledJobs.map((j) => (
                          <tr key={j.job} className="border-b border-border/40 last:border-0">
                            <td className="px-3 py-2 font-medium">{j.label}</td>
                            <td className="px-3 py-2 text-muted-foreground">{j.schedule}</td>
                            <td className="px-3 py-2 text-center">
                              {j.enabled ? (
                                <span className="text-green-600" aria-label="Enabled">
                                  ✓
                                </span>
                              ) : (
                                <span className="text-muted-foreground" aria-label="Disabled">
                                  —
                                </span>
                              )}
                            </td>
                            <td className="px-3 py-2 whitespace-nowrap text-muted-foreground">
                              {formatRunTime(j.last_run)}
                            </td>
                            <td className="px-3 py-2 whitespace-nowrap text-muted-foreground">
                              {formatRunTime(j.next_run)}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* ── Save button ──────────────────────────────────────────────────── */}
        <div className="flex items-center justify-end pt-2 pb-8">
          <Button type="submit" disabled={saving} className="min-w-[120px]">
            {saving ? 'Saving…' : 'Save Settings'}
          </Button>
        </div>
      </form>
    </div>
  );
}
